import "react-json-view-lite/dist/index.css";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";

import CopyButton from "../../../components/CopyButton";
import { IndicatorBlock } from "../../../components/IndicatorBlock";
import { ViewerArtifacts } from "../../../data/queries/viewer";
import { AvailableModel } from "../../../services/claude";
import { Artifact, getPerformanceLevel } from "../../../types/artifact";
import { formatDate } from "../../../utils/date";
import { PerformanceTab } from "../../viewer/tabs/PerformanceTab";
import { DRAWER_Z_INDEX_OFFSET } from "../constants/numeric";
import { useLensForm } from "../hooks/useLensForm";
import { useLensHistoryForInteraction } from "../hooks/useLensHistoryForInteraction";
import { useLensTester } from "../hooks/useLensTester";
import { LENS_NAVIGATION_DIRECTION, LensNavigationDirection } from "../utils/lensNavigation";
import LensHistoryModal from "./LensHistoryModal";

const ACCORDION_SUMMARY_SX = {
  minHeight: "8px !important",
  mt: "4px !important",
  mb: "4px !important",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "& .Mui-expanded": {
    mt: "12px !important",
    mb: "0px !important",
    minHeight: "8px !important"
  }
};

interface LensTesterProps {
  open: boolean;
  onClose: () => void;
  selectedArtifact: Artifact | null;
}

interface CallHeaderProps {
  data: ViewerArtifacts | null;
}

interface CallEventData {
  summary?: { header?: string; outcome?: string };
  startedAt?: string;
  isInternal?: boolean;
}

const extractCallEvent = (record: { content?: { callEvent?: CallEventData } } | null | undefined): CallEventData | null =>
  record?.content?.callEvent ?? null;

const resolveCallEvent = (data: ViewerArtifacts | null): CallEventData | null =>
  extractCallEvent(data?.callDataRecord)
    ?? extractCallEvent(data?.callRecordingEvent)
    ?? extractCallEvent(data?.callTranscriptionRecord);

interface CallHeaderData {
  formattedDate: string;
  callType: "internal" | "external";
  header: string;
  outcome: string;
  sentiment: string | undefined;
  relevance: string | undefined;
  risk: string;
  performanceLevel: string;
}

const formatStartedAt = (startedAt: string | undefined): string =>
  startedAt ? formatDate(new Date(startedAt), "dd MMMM yyyy @ h:mm a") : "";

const extractRisk = (data: ViewerArtifacts | null): string =>
  data?.riskAnalysis?.content?.riskAnalysis?.riskAnalysis?.overallIndicator ?? "N/A";

const extractSummaryInfo = (data: ViewerArtifacts | null, event: CallEventData): {
  header: string;
  outcome: string;
  sentiment: string | undefined;
  relevance: string | undefined;
} => ({
  header: event.summary?.header ?? "Call Summary",
  outcome: event.summary?.outcome ?? "No outcome data",
  sentiment: data?.summary?.content?.sentiment,
  relevance: data?.summary?.content?.relevance
});

const extractCallHeaderData = (data: ViewerArtifacts | null, event: CallEventData): CallHeaderData => ({
  formattedDate: formatStartedAt(event.startedAt),
  callType: event.isInternal ? "internal" : "external",
  ...extractSummaryInfo(data, event),
  risk: extractRisk(data),
  performanceLevel: getPerformanceLevel(data?.scorecard?.content?.scorecard?.overall_score)
});

const CALL_TYPE_CONFIG: Record<"internal" | "external", { label: string; color: string; badge: string }> = {
  internal: { label: "Internal", color: "#3f51b5", badge: "I" },
  external: { label: "External", color: "#4caf50", badge: "E" }
};

const CallTypeBadge = ({ callType }: { callType: "internal" | "external" }): React.ReactElement => {
  const config = CALL_TYPE_CONFIG[callType];
  return (
    <Tooltip title={config.label} placement="top" arrow>
      <Box sx={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 24, height: 24, borderRadius: "50%",
        backgroundColor: config.color, color: "white", fontWeight: "bold", fontSize: "12px", ml: 1
      }}>
        {config.badge}
      </Box>
    </Tooltip>
  );
};

const CallIndicators = ({ sentiment, relevance, risk, performanceLevel }: Pick<CallHeaderData, "sentiment" | "relevance" | "risk" | "performanceLevel">): React.ReactElement => (
  <Box sx={{ p: 1, mr: 2, flexWrap: "wrap", gap: 2 }}>
    <Grid container spacing={2} justifyContent="flex-end">
      <Grid size={{ xs: 6, md: 6 }}>
        {sentiment && <IndicatorBlock title="Sentiment" value={sentiment} field="sentiment" />}
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        {relevance && <IndicatorBlock title="Relevance" value={relevance} field="relevance" />}
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <IndicatorBlock title="Risk" value={risk} field="risk" />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }}>
        <IndicatorBlock title="Performance" value={performanceLevel} field="performance" />
      </Grid>
    </Grid>
  </Box>
);

const CallHeader = ({ data }: CallHeaderProps): React.ReactElement | null => {
  const event = resolveCallEvent(data);

  if (!event) {
    return null;
  }

  const info = extractCallHeaderData(data, event);

  return (
    <Box sx={{ p: 2, bgcolor: "#FFF", borderRadius: "4px 4px 0 0" }}>
      <Grid container spacing={2} justifyContent="space-between">
        <Grid size="grow">
          <Stack spacing={1}>
            <Typography variant="h6" component="h2">{info.header}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Called {info.formattedDate}. {info.outcome}
              </Typography>
              <CallTypeBadge callType={info.callType} />
            </Stack>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 8, lg: 6 }}>
          <CallIndicators sentiment={info.sentiment} relevance={info.relevance} risk={info.risk} performanceLevel={info.performanceLevel} />
        </Grid>
      </Grid>
    </Box>
  );
};

interface CurrentLensPanelProps {
  currentLensExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  currentLensName: string | undefined;
  isLoadingLensData: boolean;
  lensContent: string;
}

const MULTILINE_INPUT_SX = {
  height: "calc(100% - 16px)",
  alignItems: "flex-start",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "& .MuiInputBase-inputMultiline": { height: "100% !important", overflow: "auto !important" }
};

const MULTILINE_FIELD_SX = {
  height: "calc(100% - 16px)",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "& .MuiOutlinedInput-root": { height: "100%" }
};

const LensLoadingSkeleton = (): React.ReactElement => (
  <Box sx={{ width: "100%", height: "calc(100% - 16px)" }}>
    <Skeleton variant="text" sx={{ fontSize: "1rem", mb: 1 }} />
    <Skeleton variant="rectangular" height="calc(100% - 32px)" />
  </Box>
);

const LensContentViewer = ({ content }: { content: string }): React.ReactElement => (
  <>
    <TextField
      multiline fullWidth value={content}
      placeholder="Current lens content will be loaded here..."
      InputProps={{ readOnly: true, sx: MULTILINE_INPUT_SX }}
      sx={MULTILINE_FIELD_SX}
    />
    <Box sx={{ position: "absolute", top: 8, right: 32, zIndex: 1 }}>
      <CopyButton text={content} tooltipTitle="Copy current lens" richText={false} />
    </Box>
  </>
);

const CurrentLensPanel = ({
  currentLensExpanded, onExpandedChange, currentLensName, isLoadingLensData, lensContent
}: CurrentLensPanelProps): React.ReactElement => (
  <Accordion expanded={currentLensExpanded} onChange={(_, isExpanded) => onExpandedChange(isExpanded)}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="current-lens-content" id="current-lens-header" sx={ACCORDION_SUMMARY_SX}>
      <Typography variant="h6">Current Lens</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ height: "550px", overflow: "hidden", p: 0 }}>
      <Stack spacing={0} sx={{ height: "100%", p: 2, pt: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "32px", mt: 2.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {currentLensName || "No current lens"}
          </Typography>
        </Box>
        <Box sx={{ position: "relative", height: "100%", p: 0 }}>
          {isLoadingLensData ? <LensLoadingSkeleton /> : <LensContentViewer content={lensContent} />}
        </Box>
      </Stack>
    </AccordionDetails>
  </Accordion>
);

interface TestLensPanelProps {
  testLensExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  form: ReturnType<typeof useLensForm>;
  interactionHistory: ReturnType<typeof useLensHistoryForInteraction>;
  onNavigate: (direction: LensNavigationDirection) => void;
  selectedLensPath: string;
  isLoadingLens: boolean;
  lensData: { currentLens: string; consolidatedLensList: Array<{ fullPath: string; category: string; filename: string }> } | undefined;
  onLensSelection: (path: string) => void;
  isTestLoading: boolean;
  onTest: () => void;
}

interface VersionNavigationProps {
  onNavigate: (direction: LensNavigationDirection) => void;
  canGoBack: boolean;
  canGoForward: boolean;
  versionDisplay: string;
}

const VersionNavigation = ({ onNavigate, canGoBack, canGoForward, versionDisplay }: VersionNavigationProps): React.ReactElement => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <Tooltip title="Previous lens" placement="top">
      <span>
        <IconButton size="small" onClick={() => onNavigate(LENS_NAVIGATION_DIRECTION.Previous)} disabled={!canGoBack} sx={{ p: 0.5 }}>
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
    <Typography variant="caption" color="text.secondary" sx={{ mx: 1, minWidth: "40px", textAlign: "center" }}>
      {versionDisplay}
    </Typography>
    <Tooltip title="Next lens" placement="top">
      <span>
        <IconButton size="small" onClick={() => onNavigate(LENS_NAVIGATION_DIRECTION.Next)} disabled={!canGoForward} sx={{ p: 0.5 }}>
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </Box>
);

const TEST_EDITOR_INPUT_SX = {
  height: "calc(100% - 112px)",
  alignItems: "flex-start",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "& .MuiInputBase-inputMultiline": { height: "100% !important", overflow: "auto !important" }
};

const TEST_EDITOR_FIELD_SX = {
  height: "calc(100% - 112px)",
  // eslint-disable-next-line @typescript-eslint/naming-convention
  "& .MuiOutlinedInput-root": { height: "100%" }
};

interface TestLensToolbarProps {
  form: ReturnType<typeof useLensForm>;
  selectedLensPath: string;
  isLoadingLens: boolean;
  lensData: TestLensPanelProps["lensData"];
  onLensSelection: (path: string) => void;
  isTestLoading: boolean;
  onTest: () => void;
}

const TestLensToolbar = ({
  form, selectedLensPath, isLoadingLens, lensData, onLensSelection, isTestLoading, onTest
}: TestLensToolbarProps): React.ReactElement => (
  <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexShrink: 0, height: "56px", mt: 2 }}>
    <FormControl size="small" sx={{ minWidth: 250 }}>
      <InputLabel>Load Lens</InputLabel>
      <Select value={selectedLensPath} label="Load Lens" onChange={(e) => onLensSelection(e.target.value)} disabled={isLoadingLens}>
        <MenuItem value=""><em>Select a lens to load...</em></MenuItem>
        {lensData?.consolidatedLensList.map((lens) => (
          <MenuItem key={lens.fullPath} value={lens.fullPath}>{lens.category}/{lens.filename}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel>Model</InputLabel>
      <Select value={form.model} label="Model" onChange={(e) => form.setModel(e.target.value as AvailableModel)}>
        {form.availableModels.map((model) => (
          <MenuItem key={model.id} value={model.id}>{model.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
    <TextField size="small" label="Token Count" type="number" value={form.tokenCount} onChange={(e) => form.setTokenCount(Number(e.target.value))} sx={{ width: 120 }} />
    <TextField size="small" label="Temperature" type="number" inputProps={{ min: 0, max: 1, step: 0.1 }} value={form.temperature} onChange={(e) => form.setTemperature(Number(e.target.value))} sx={{ width: 120 }} />
    <Button variant="contained" onClick={onTest} disabled={isTestLoading || !form.testLens.trim() || form.lensName.trim() === ""} startIcon={isTestLoading ? <CircularProgress size={20} /> : null}>
      Test
    </Button>
  </Box>
);

const TestLensPanel = ({
  testLensExpanded, onExpandedChange, form, interactionHistory, onNavigate,
  selectedLensPath, isLoadingLens, lensData, onLensSelection, isTestLoading, onTest
}: TestLensPanelProps): React.ReactElement => (
  <Accordion expanded={testLensExpanded} onChange={(_, isExpanded) => onExpandedChange(isExpanded)}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="test-lens-content" id="test-lens-header" sx={ACCORDION_SUMMARY_SX}>
      <Typography variant="h6">Test Lens</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ height: "550px", overflow: "hidden", p: 0 }}>
      <Stack spacing={0} sx={{ height: "100%", p: 2, pt: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "32px", mb: 0.5, mt: 1 }}>
          <TextField
            size="small" label="Test Name & version" value={form.lensName}
            onChange={(e) => form.setLensName(e.target.value)} placeholder="Enter test name & version..."
            error={form.lensName.trim() === ""} sx={{ flex: 1, maxWidth: "500px" }}
          />
          <VersionNavigation
            onNavigate={onNavigate} canGoBack={interactionHistory.canGoBack}
            canGoForward={interactionHistory.canGoForward} versionDisplay={interactionHistory.versionDisplay}
          />
        </Box>
        <TextField
          multiline fullWidth value={form.testLens}
          onChange={(e) => form.setTestLens(e.target.value)} placeholder="Enter your test lens content here..."
          InputProps={{ sx: TEST_EDITOR_INPUT_SX }} sx={TEST_EDITOR_FIELD_SX}
        />
        <TestLensToolbar
          form={form} selectedLensPath={selectedLensPath} isLoadingLens={isLoadingLens}
          lensData={lensData} onLensSelection={onLensSelection} isTestLoading={isTestLoading} onTest={onTest}
        />
      </Stack>
    </AccordionDetails>
  </Accordion>
);

interface LensTesterHeaderProps {
  interactionId: string | null;
  isHistoryEmpty: boolean;
  onOpenHistory: () => void;
  onClose: () => void;
}

const LensTesterHeader = ({ interactionId, isHistoryEmpty, onOpenHistory, onClose }: LensTesterHeaderProps): React.ReactElement => (
  <Box sx={{
    display: "flex", justifyContent: "space-between", alignItems: "center",
    p: 2, pt: 3, mt: 7, borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
  }}>
    <Typography variant="h6" component="div">
      <strong>Lens Tester</strong>{interactionId ? `: ${interactionId}` : ""}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Tooltip title="Test results for ALL lenses" placement="bottom">
        <IconButton onClick={onOpenHistory} aria-label="view all lens test results" color="primary" disabled={isHistoryEmpty}>
          <HistoryIcon />
        </IconButton>
      </Tooltip>
      <IconButton onClick={onClose} aria-label="close lens tester" color="primary">
        <CloseIcon />
      </IconButton>
    </Box>
  </Box>
);

interface LensTesterContentProps {
  state: ReturnType<typeof useLensTester>;
}

const LensTesterContent = ({ state }: LensTesterContentProps): React.ReactElement => (
  <Box sx={{ flex: 1, p: 2, pt: 1, overflow: "auto" }}>
    <Grid container spacing={2} sx={{ height: "100%" }}>
      <Grid size={{ xs: 6, md: 6 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <CurrentLensPanel
          currentLensExpanded={state.currentLensExpanded}
          onExpandedChange={state.setCurrentLensExpanded}
          currentLensName={state.currentLensName}
          isLoadingLensData={state.isLoadingLensData}
          lensContent={state.lensData?.currentLens || ""}
        />
        <PerformanceTab data={state.artifactData} isLoading={state.isLoadingLensData} expandedAttribute="current_lens" />
      </Grid>
      <Grid size={{ xs: 6, md: 6 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <TestLensPanel
          testLensExpanded={state.testLensExpanded}
          onExpandedChange={state.setTestLensExpanded}
          form={state.form}
          interactionHistory={state.interactionHistory}
          onNavigate={state.handleNavigate}
          selectedLensPath={state.selectedLensPath}
          isLoadingLens={state.isLoadingLens}
          lensData={state.lensData}
          onLensSelection={state.handleLensSelection}
          isTestLoading={state.lensTestWithHistory.isLoading}
          onTest={state.handleTest}
        />
        <PerformanceTab data={state.testResultsData} isLoading={state.lensTestWithHistory.isLoading} expandedAttribute="test_lens" />
      </Grid>
    </Grid>
  </Box>
);

export const LensTester = ({ open, onClose, selectedArtifact }: LensTesterProps): React.ReactElement => {
  const state = useLensTester({ open, selectedArtifact });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        width: "90%",
        flexShrink: 0,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "& .MuiDrawer-paper": {
          width: "90%",
          boxSizing: "border-box",
          zIndex: (theme) => theme.zIndex.drawer + DRAWER_Z_INDEX_OFFSET
        }
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <LensTesterHeader
          interactionId={state.interactionId}
          isHistoryEmpty={state.isHistoryEmpty}
          onOpenHistory={state.historyModal.handleOpen}
          onClose={onClose}
        />
        <Paper elevation={1} sx={{ m: 2, mb: 1 }}>
          <CallHeader data={state.artifactData} />
        </Paper>
        <LensTesterContent state={state} />
      </Box>

      <LensHistoryModal
        open={state.historyModal.open}
        onClose={() => state.historyModal.setOpen(false)}
        currentItem={state.historyModal.currentItem}
        modalIndex={state.historyModal.modalIndex}
        totalCount={state.allHistoryLength}
        onNavigate={state.historyModal.handleNavigate}
      />
    </Drawer>
  );
};

export default LensTester;
